/**
 * RewardsFAB - Bouton flottant d'accès aux récompenses
 *
 * Affiche un bouton animé avec le nombre de coins
 * et un indicateur si une récompense gratuite est disponible
 */

import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Coins, Sparkles } from 'lucide-react';

export const RewardsFAB = memo(function RewardsFAB({
  availableCoins,
  hasFreeReward,
  onClick,
  isCompact = false,
}) {
  const [showCoins, setShowCoins] = useState(false);
  const [prevCoins, setPrevCoins] = useState(availableCoins);

  // Animation quand les coins changent
  useEffect(() => {
    if (availableCoins > prevCoins) {
      setShowCoins(true);
      const timer = setTimeout(() => setShowCoins(false), 2000);
      return () => clearTimeout(timer);
    }
    setPrevCoins(availableCoins);
  }, [availableCoins, prevCoins]);

  if (isCompact) {
    // Version compacte pour la barre de navigation
    return (
      <button
        onClick={onClick}
        className="relative flex items-center gap-2 px-3 py-2 bg-gradient-to-r
          from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30
          hover:border-purple-400/50 transition-all"
      >
        <Gift className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-bold text-purple-300">{availableCoins}</span>

        {hasFreeReward && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
        )}
      </button>
    );
  }

  // Version FAB (bouton flottant)
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="relative"
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-lg opacity-50" />

      {/* Button */}
      <div className={`
        relative w-14 h-14 rounded-full
        bg-gradient-to-br from-purple-500 to-pink-500
        flex items-center justify-center
        shadow-lg shadow-purple-500/30
        ${hasFreeReward ? 'animate-bounce' : ''}
      `}>
        <Gift className="w-6 h-6 text-white" />

        {/* Badge avec coins */}
        <div className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5
          bg-yellow-500 rounded-full flex items-center justify-center gap-0.5">
          <Coins className="w-3 h-3 text-yellow-900" />
          <span className="text-[10px] font-bold text-yellow-900">
            {availableCoins > 999 ? '999+' : availableCoins}
          </span>
        </div>

        {/* Indicateur récompense gratuite */}
        {hasFreeReward && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -bottom-1 -left-1 w-6 h-6 bg-emerald-500 rounded-full
              flex items-center justify-center shadow-lg"
          >
            <Sparkles className="w-3 h-3 text-white" />
          </motion.div>
        )}
      </div>

      {/* Animation coins gagnés */}
      <AnimatePresence>
        {showCoins && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.5 }}
            animate={{ opacity: 1, y: -20, scale: 1 }}
            exit={{ opacity: 0, y: -30 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 whitespace-nowrap
              px-2 py-1 bg-yellow-500 rounded-full text-xs font-bold text-yellow-900"
          >
            +{availableCoins - prevCoins} coins
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
});

export default RewardsFAB;
